import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

const HIBOTICS_LOGO_DARK = '/hibotics_ai_logo_transparent.png';
const HIBOTICS_LOGO_LIGHT = '/hibotics_ai_logo_dark_bg.png';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Theme is locked to dark — references and current design only support dark mode.
  // Toggle UI has been removed from sidebar + settings.
  const [theme] = useState('dark');

  // White-label branding state
  const [branding, setBranding] = useState({
    brandName: 'HiBotics AI',
    brandLogoUrlDark: HIBOTICS_LOGO_DARK,
    brandLogoUrlLight: HIBOTICS_LOGO_LIGHT,
    primaryColor: '#00FFD1',
    accentColor: '#00FFD1',
    showPoweredBy: true
  });

  // Always use the dark logo
  const currentLogo = branding.brandLogoUrlDark;

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light');
    root.classList.add('dark');
    // Clear any stale "light" preference from older builds
    if (localStorage.getItem('hibotics-theme') !== 'dark') {
      localStorage.setItem('hibotics-theme', 'dark');
    }
  }, []);

  // Apply branding colors as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    
    if (branding.primaryColor) {
      // Convert hex to HSL for CSS variable
      const primaryHsl = hexToHsl(branding.primaryColor);
      root.style.setProperty('--custom-primary', primaryHsl);
    }
    
    if (branding.accentColor) {
      const accentHsl = hexToHsl(branding.accentColor);
      root.style.setProperty('--custom-accent', accentHsl);
    }
  }, [branding]);

  const applyBranding = (newBranding) => {
    setBranding(prev => ({ ...prev, ...newBranding }));
  };

  const value = {
    theme,
    isDark: true,
    branding: {
      ...branding,
      brandLogoUrl: currentLogo
    },
    applyBranding
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Helper function to convert hex to HSL string
function hexToHsl(hex) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default ThemeContext;
